import { Tooltip } from 'uikit';
import type { Direction, PropGetItemKey } from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import type { IMultiselectItem, IMultiselectItemInformer } from '../Multiselect/Multiselect';
import { Multiselect } from '../Multiselect/Multiselect';

interface IControlledMultiselectField extends UseControllerProps<any> {
    label?: string;
    labelPosition?: 'left' | 'top';
    disabled?: boolean;
    required?: boolean;
    items: IMultiselectItem[];
    isTooltipHidden?: boolean;
    classNameContainer?: string;
    classNameTooltip?: string;
    disabledIds?: (number | string)[];
    disabledItemOnHoverMessage?: string;
    hideRemoveIfDisabled?: boolean;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    direction?: Direction;
    getItemKey?: PropGetItemKey<IMultiselectItem>;
    getItemLabel?: (item: any) => string;
    informers?: IMultiselectItemInformer[];
    placeholder?: string;
}

export const ControlledMultiselectField: React.FC<IControlledMultiselectField> = (props) => {
    const {
        name,
        control,
        rules,
        items,
        disabled,
        labelPosition,
        label,
        required,
        isTooltipHidden,
        classNameContainer,
        classNameTooltip,
        disabledIds,
        disabledItemOnHoverMessage,
        hideRemoveIfDisabled,
        direction = 'rightCenter',
        onTooltipAttributeChange,
        getItemKey,
        getItemLabel,
        informers,
        placeholder,
    } = props;

    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const onChange = useCallback(
        (value) => {
            field.onChange(value);
        },
        [field],
    );

    const errorMessage = useMemo(
        () => (fieldState.invalid && fieldState.error?.message) || '',
        [fieldState.error?.message, fieldState.invalid],
    );

    const tooltipRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    return (
        <div className={classNameContainer}>
            <Multiselect
                onChange={onChange}
                selectedItems={field.value}
                items={items}
                status={errorMessage ? 'alert' : undefined}
                label={label}
                disabled={disabled}
                labelPosition={labelPosition}
                required={required}
                disabledIds={disabledIds}
                disabledItemOnHoverMessage={disabledItemOnHoverMessage}
                hideRemoveIfDisabled={hideRemoveIfDisabled}
                multiselectRef={anchorRef}
                getItemKey={getItemKey}
                getItemLabel={getItemLabel}
                informers={informers}
                placeholder={placeholder}
            />
            {!isTooltipHidden && errorMessage && (
                <Tooltip
                    size="s"
                    anchorRef={anchorRef}
                    direction={direction}
                    possibleDirections={[direction]}
                    status="alert"
                    className={`${classNameTooltip} tooltip-${name}`}
                    ref={tooltipRef}
                >
                    {errorMessage}
                </Tooltip>
            )}
        </div>
    );
};

ControlledMultiselectField.displayName = 'ControlledMultiselectField';
